import { useEffect, useState } from "react";
import { useAxios } from "../../Helpers/AxiosHelper";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "../../Helpers/ToastHelper";
import { useTranslation } from "react-i18next";
import {
    Button,
    DDBrands,
    DDContactType,
    DDCountries,
    FloatLabel,
    InputText,
    ProgressSpinner,
    SCIcon,
    SubmitHandler,
} from "../../Common/Imports";
import { ApiResponse, Contacts_EXT } from "../../Models/Classes";

interface ContactEditProps {
    stakeholderId?: number; // Make it optional
    contactId?: number; // Make it optional
    OnSave?: () => void;
}

const ContactEdit: React.FC<ContactEditProps> = ({ stakeholderId, contactId, OnSave }) => {
    const [resolvedStakeholderId, setResolvedStakeholderId] = useState<number | null | undefined>(stakeholderId);

    const { t } = useTranslation();
    const { showToast } = useToast();
    const {
        reset,
        handleSubmit,
        control,
    } = useForm<Contacts_EXT>({
        defaultValues: {
            value: "",
            type: 1,
            idCountry: undefined,
            idStakeholder: stakeholderId
        },
    });

    const AxiosHelper = useAxios();
    const [loading, setLoading] = useState<boolean>(!!contactId); // Only load if editing

    useEffect(() => {
        if (contactId) {
            const fetchContact = async () => {
                try {
                    const response = await AxiosHelper.get<ApiResponse<Contacts_EXT>>(`Contacts/Get/${contactId}`);
                    setResolvedStakeholderId(response.data.data.idStakeholder);
                    reset(response.data.data);
                } catch (error) {
                    console.error("Error fetching contact data:", error);
                    showToast("error", "Error", t("Common.Toasts.Fetch.Error"));
                } finally {
                    setLoading(false);
                }
            };
            fetchContact();
        } else {
            setLoading(false);
        }
    }, [reset, AxiosHelper, t, showToast, contactId]);

    const onSubmit: SubmitHandler<Contacts_EXT> = async (formData) => {
        try {
            if (contactId) {
                // Update existing contact
                await AxiosHelper.post(`Contacts/UpdateContact`, formData);
                showToast("success", "Success", t("Common.Toasts.Update.Success"));
            } else {
                // Create new contact
                await AxiosHelper.post(`Contacts/AddContact`, formData);
                showToast("success", "Success", t("Common.Toasts.New.Success"));
            }
            if (OnSave) OnSave();
        } catch (error) {
            console.log(error);
            showToast("error", "Error", t("Common.Toasts.New.Error"));
        }
    };

    if (loading) return <ProgressSpinner />;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid" style={{ minWidth: "1000px" }}>
            <div className="row col-12">
                <Controller
                    name="value"
                    control={control}
                    rules={{ required: `${t("Contacts.Value")} ${t("Common.Form.Required")}` }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-6 mb-4">
                            <FloatLabel>
                                <InputText {...field} value={field.value} onBlur={field.onBlur} />
                                <label>{t("Contacts.Value")}</label>
                            </FloatLabel>
                            {fieldState.invalid && <small className="p-error">{fieldState.error?.message}</small>}
                        </div>
                    )}
                />
                <Controller
                    name="type"
                    control={control}
                    rules={{ required: `${t("Contacts.Type")} ${t("Common.Form.Required")}` }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-6 mb-4">
                            <FloatLabel>
                                <DDContactType
                                    {...field}
                                    value={field.value}
                                    onSelect={(newValue) => field.onChange(newValue)}
                                    onBlur={field.onBlur}
                                />
                                <label>{t("Contacts.Type")}</label>
                            </FloatLabel>
                            {fieldState.invalid && <small className="p-error">{fieldState.error?.message}</small>}
                        </div>
                    )}
                />
            </div>
            <div className="row col-12 mb-3">
                <Controller
                    name="idCountry"
                    control={control}
                    render={({ field, fieldState }) => (
                        <div className="col-md-6">
                            <FloatLabel>
                                <DDCountries
                                    {...field}
                                    showClear
                                    filter
                                    value={field.value ?? undefined}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    onBlur={field.onBlur}
                                />
                                <label>{t("Contacts.Location")}</label>
                            </FloatLabel>
                            {fieldState.invalid && <small className="p-error">{fieldState.error?.message}</small>}
                        </div>
                    )}
                />
                <Controller
                    name="idBrand"
                    control={control}
                    render={({ field, fieldState }) => (
                        <div className="col-md-6">
                            <FloatLabel>
                                <DDBrands
                                    {...field}
                                    showClear
                                    IdStakeholder={resolvedStakeholderId}
                                    value={field.value ?? undefined}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    onBlur={field.onBlur}
                                />
                                <label>{t("Contacts.Brand")}</label>
                            </FloatLabel>
                            {fieldState.invalid && <small className="p-error">{fieldState.error?.message}</small>}
                        </div>
                    )}
                />
            </div>
            <div className="row">
                <div className="col-md-12">
                    <Button type="submit" label={t("Common.Buttons.Save")} icon={<SCIcon Name="Save" />} />
                </div>
            </div>
        </form>
    );
};

export default ContactEdit;
